import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Instagram, Youtube, Linkedin, Twitter, Facebook, ArrowUpRight } from "lucide-react";
import { services } from "@/data/services";
import NewsletterForm from "@/components/NewsletterForm";

// Custom inline TikTok icon (lucide-react no longer ships brand icons)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.55a8.16 8.16 0 0 0 4.77 1.52V6.69z"/>
  </svg>
);

const Footer = () => {
  const { t, lang } = useLang();

  const socials = [
    { icon: Facebook, href: "https://www.facebook.com/AlmkhrjHanyHjazy?locale=ar_AR", label: "Facebook" },
    { icon: TikTokIcon, href: "https://www.tiktok.com/@dir.hani.hejazi", label: "TikTok" },
    { icon: Twitter, href: "https://x.com/HaniHejazi2025", label: "X" },
    { icon: Instagram, href: "https://www.instagram.com/hani_art_lenss/", label: "Instagram" },
    { icon: Youtube, href: "https://www.youtube.com/@artlenss2026", label: "YouTube" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/%D8%A7%D9%84%D9%85%D8%AE%D8%B1%D8%AC-%D9%87%D8%A7%D9%86%D9%8A-%D8%AD%D8%AC%D8%A7%D8%B2%D9%8A-26668378/", label: "LinkedIn" },
  ];

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-[#0A0A0C]">
      {/* Newsletter — cinematic top strip */}
      <div className="border-b border-white/5 bg-gradient-to-b from-[#0d0d10] to-[#0A0A0C]">
        <div className="container-x py-14 md:py-16">
          <NewsletterForm />
        </div>
      </div>
      <div className="container-x py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 text-center">
          <Link to="/" className="inline-flex items-center gap-4 justify-center">
            <img
              src="/brand/logo.png"
              alt="ARTLENSS"
              className="h-16 md:h-20 w-auto object-contain"
            />
          </Link>
          <p className="mt-6 text-white/60 max-w-md text-lg leading-relaxed mx-auto">{t("footer.tagline")}</p>
          <div className="mt-8 flex items-center gap-3 justify-center">
            {socials.map((s) => {
              const isExternal = s.href.startsWith("http");
              return (
                <a
                  key={s.label}
                  href={s.href}
                  aria-label={s.label}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-11 h-11 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-[#E6A95B] hover:border-[#E6A95B]/50 transition"
                >
                  <s.icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-3 text-center">
          <div className="overline mb-5">{t("footer.quickLinks")}</div>
          <ul className="space-y-3 text-white/70">
            {[
              ["/", t("nav.home")],
              ["/about", t("nav.about")],
              ["/work", t("nav.work")],
              ["/services", t("nav.services")],
              ["/methodology", t("nav.methodology")],
              ["/awards", t("nav.awards")],
              ["/blog", t("nav.blog")],
              ["/contact", t("nav.contact")],
            ].map(([to, label]) => (
              <li key={to}>
                <Link to={to} className="link-underline hover:text-[#E6A95B]">{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2 text-center">
          <div className="overline mb-5">{t("footer.services")}</div>
          <ul className="space-y-3 text-white/70 text-sm">
            {services.map((s) => (
              <li key={s.id}>
                <Link to={`/services#${s.id}`} className="link-underline hover:text-[#E6A95B]">
                  {s.title[lang]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2 text-center">
          <div className="overline mb-5">{t("footer.contact")}</div>
          <ul className="space-y-3 text-white/70 text-sm">
            <li><a href="mailto:info@artlenss.com" className="link-underline">info@artlenss.com</a></li>
            <li dir="ltr" className="text-[#E6A95B] font-semibold">+966 55 414 2007</li>
            <li>{t("contact.location")}</li>
          </ul>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-1 text-[#E6A95B] group justify-center">
            <span className="font-semibold text-sm tracking-wide">{t("nav.cta")}</span>
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 -translate-y-0.5 transition" />
          </Link>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="container-x py-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-white/40 text-xs">
          <span>© {new Date().getFullYear()} Art Lens. {t("footer.rights")}</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#E6A95B] transition">
              {t("footer.privacy")}
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#E6A95B] transition">
              {t("footer.terms")}
            </Link>
          </div>
          <span className="hidden md:inline">{t("footer.built")}</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
